import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import moment from "moment";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LocalStorageService } from "../../local-storage/local-storage.service";
import { PadlockService } from "../../padlock/padlock.service";
import { SetActiveWorkspace } from "../state/workspaces.actions";
import {
  partitionWorkspacesByOwnership,
  Workspace,
  WorkspaceDetails,
} from "../state/workspaces.model";
import { WorkspacesState } from "../state/workspaces.reducer";
import { selectWorkspacesState } from "../state/workspaces.selectors";
import { WorkspaceService } from "../workspace.service";

// Deterministic, branded avatar colors (cycled by hashing the workspace uuid)
const AVATAR_PALETTE = [
  "avatar-ultramarine",
  "avatar-jade",
  "avatar-tangerine",
  "avatar-violet",
  "avatar-green",
  "avatar-pink",
  "avatar-infoBlue",
  "avatar-seafoam",
];

const MAX_RECENT_ENTRIES = 10;
const RECENT_RAIL_SIZE = 6;

interface RecentEntry {
  uuid: string;
  visitedAt: number;
}

@Component({
  selector: "app-workspaces",
  templateUrl: "./workspaces.component.html",
  styleUrls: ["./workspaces.component.scss"],
  standalone: false,
})
export class WorkspacesComponent implements OnInit, OnDestroy {
  workspaces: Workspace[] | null = null;
  myWorkspaces: Workspace[] = [];
  otherWorkspaces: Workspace[] = [];
  filteredMyWorkspaces: Workspace[] = [];
  filteredOtherWorkspaces: Workspace[] = [];
  recentWorkspaces: Workspace[] = [];

  loading = false;
  loadError: any = null;
  userID: string | null = null;
  isPlatformAdmin = false;
  searchText = "";

  workspacesState$: Observable<WorkspacesState> = this.store.select(
    selectWorkspacesState,
  );

  destroyed$ = new Subject<boolean>();

  private favoriteIds = new Set<string>();
  private recentEntries: RecentEntry[] = [];
  private favoritesKey = "workspaces-favorites";
  private recentsKey = "workspaces-recents";

  constructor(
    private store: Store<any>,
    private padlock: PadlockService,
    private workspaceService: WorkspaceService,
    private localStorage: LocalStorageService,
  ) {}

  ngOnInit() {
    const decoded = this.padlock.getDecodedToken();
    this.userID = decoded ? decoded.sub : null;
    this.isPlatformAdmin = decoded?.platform_admin === true;

    const userScope = this.userID ?? "anonymous";
    this.favoritesKey = `workspaces-favorites:${userScope}`;
    this.recentsKey = `workspaces-recents:${userScope}`;
    this.loadFavorites();
    this.loadRecents();

    this.workspacesState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((workspaceState) => {
        this.workspaces = workspaceState.workspaces;
        const { mine, other } = partitionWorkspacesByOwnership(
          workspaceState.workspaces || [],
          this.userID,
        );
        this.myWorkspaces = this.sortWorkspaces(mine);
        this.otherWorkspaces = this.sortWorkspaces(other);
        this.updateRecentWorkspaces();
        this.applySearch();
        this.loading = workspaceState.loading;
        this.loadError = workspaceState.loadError;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  activate(workspace: Workspace) {
    if (workspace?.uuid) {
      this.recordRecent(workspace.uuid);
    }
    this.store.dispatch(new SetActiveWorkspace(workspace, true));
  }

  removeRecent(workspace: Workspace, event: Event) {
    event.stopPropagation();
    if (!workspace?.uuid) {
      return;
    }
    this.recentEntries = this.recentEntries.filter(
      (entry) => entry.uuid !== workspace.uuid,
    );
    this.persistRecents();
    this.updateRecentWorkspaces();
  }

  newWorkspace() {
    this.workspaceService.newWorkspace();
  }

  onSearchChange(value: string) {
    this.searchText = value;
    this.applySearch();
  }

  clearSearch() {
    this.onSearchChange("");
  }

  toggleFavorite(workspace: Workspace, event: Event) {
    event.stopPropagation();
    if (!workspace?.uuid) {
      return;
    }
    if (this.favoriteIds.has(workspace.uuid)) {
      this.favoriteIds.delete(workspace.uuid);
    } else {
      this.favoriteIds.add(workspace.uuid);
    }
    this.persistFavorites();
    this.myWorkspaces = this.sortWorkspaces(this.myWorkspaces);
    this.otherWorkspaces = this.sortWorkspaces(this.otherWorkspaces);
    this.applySearch();
  }

  isFavorite(workspace: Workspace): boolean {
    return !!workspace?.uuid && this.favoriteIds.has(workspace.uuid);
  }

  getInitials(workspace: Workspace): string {
    const source = (workspace?.label || workspace?.name || "").trim();
    if (!source) {
      return "?";
    }
    const words = source.split(/\s+/).filter(Boolean);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }

  getAvatarClass(workspace: Workspace): string {
    const source = workspace?.uuid || workspace?.label || "";
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
      hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
    }
    return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  }

  getLastVisitedLabel(workspace: Workspace): string | null {
    const entry = this.recentEntries.find((e) => e.uuid === workspace?.uuid);
    if (!entry) {
      return null;
    }
    return moment(entry.visitedAt).fromNow();
  }

  // Only present when the platform-admin /workspace/details response backs this list.
  getDatasourceCount(workspace: Workspace): number | null {
    return this.readDetailCount(workspace, "datasource_count");
  }

  getModelCount(workspace: Workspace): number | null {
    return this.readDetailCount(workspace, "model_count");
  }

  getMostRecentApiRequestLabel(workspace: Workspace): string | null {
    const value = (workspace as Partial<WorkspaceDetails>)
      .most_recent_api_request;
    return value ? moment(value).fromNow() : null;
  }

  getMostRecentApiRequestTooltip(workspace: Workspace): string | null {
    const value = (workspace as Partial<WorkspaceDetails>)
      .most_recent_api_request;
    return value ? `Last API request: ${moment(value).format("LLL")}` : null;
  }

  getOwnerEmail(workspace: Workspace): string | null {
    return (workspace as Partial<WorkspaceDetails>).owner_email || null;
  }

  private readDetailCount(
    workspace: Workspace,
    field: "datasource_count" | "model_count",
  ): number | null {
    const value = (workspace as Partial<WorkspaceDetails>)[field];
    return typeof value === "number" ? value : null;
  }

  private sortWorkspaces(workspaces: Workspace[]): Workspace[] {
    return [...workspaces].sort((a, b) => {
      const aFavoriteRank = this.isFavorite(a) ? 0 : 1;
      const bFavoriteRank = this.isFavorite(b) ? 0 : 1;
      if (aFavoriteRank !== bFavoriteRank) {
        return aFavoriteRank - bFavoriteRank;
      }
      return (a.label || "").localeCompare(b.label || "");
    });
  }

  private applySearch() {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      this.filteredMyWorkspaces = this.myWorkspaces;
      this.filteredOtherWorkspaces = this.otherWorkspaces;
      return;
    }
    const matches = (workspace: Workspace) =>
      (workspace.label || "").toLowerCase().includes(term) ||
      (workspace.name || "").toLowerCase().includes(term);
    this.filteredMyWorkspaces = this.myWorkspaces.filter(matches);
    this.filteredOtherWorkspaces = this.otherWorkspaces.filter(matches);
  }

  private recordRecent(uuid: string) {
    this.recentEntries = [
      { uuid, visitedAt: Date.now() },
      ...this.recentEntries.filter((entry) => entry.uuid !== uuid),
    ].slice(0, MAX_RECENT_ENTRIES);
    this.persistRecents();
    this.updateRecentWorkspaces();
  }

  private updateRecentWorkspaces() {
    const combined = [...this.myWorkspaces, ...this.otherWorkspaces];
    this.recentWorkspaces = this.recentEntries
      .map((entry) => combined.find((w) => w.uuid === entry.uuid))
      .filter((workspace): workspace is Workspace => !!workspace)
      .slice(0, RECENT_RAIL_SIZE);
  }

  private loadFavorites() {
    try {
      const stored = this.localStorage.load(this.favoritesKey);
      this.favoriteIds = new Set(Array.isArray(stored) ? stored : []);
    } catch {
      this.favoriteIds = new Set();
    }
  }

  private persistFavorites() {
    this.localStorage.persist(
      this.favoritesKey,
      Array.from(this.favoriteIds),
    );
  }

  private loadRecents() {
    try {
      const stored = this.localStorage.load(this.recentsKey);
      this.recentEntries = Array.isArray(stored) ? stored : [];
    } catch {
      this.recentEntries = [];
    }
  }

  private persistRecents() {
    this.localStorage.persist(this.recentsKey, this.recentEntries);
  }
}
